import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../../config/config';

export interface TermFee {
  TermName: string;
  feesTermAmount: number;
  Balance: number;
  Paid: number;
  discount: number;
  DueDate: string;
  lateFee?: number;
  pkLateFeesId?: number | null;
  fkFeesTermId: number;
  fkFeesTypeId?: number;
  fkFeeStructuresId?: number | null;
  timeLeftToPayAgain: string | null;
}

export interface TypeFee {
  Name: string;
  feesTypeAmount: number;
  Balance: number;
  Paid: number;
  Discount: number;
  DueDate: string;
  fkFeesTypeId: number;
  isBusFee: number;
  busId: string | null;
  busTermId?: number | null;
  busStopId?: number | null;
  fkFeeStructuresId?: number | null;
  lateFee?: number;
  pkLateFeesId?: number | null;
  timeLeftToPayAgain?: string | null;
}

export interface OtherFeeTerm {
  Name: string;
  termAmount: number;
  termDueDate: string;
  Balance: number;
  Paid: number;
  Discount: number;
  pkOtherFeesId: number;
  pkOtherFeeTermId: number;
  fkFeeStructuresId?: number | null;
  lateFee?: number;
  pkLateFeesId?: number | null;
  timeLeftToPayAgain?: string | null;
}

export interface FeesListBody {
  termFees: TermFee[];
  typeFees: TypeFee[];
  otherFeeTerms: OtherFeeTerm[];
  pendingPaymentBlockDurationMinutes: number;
}

export interface FeeHistoryEntry {
  totalAmount: number;
  ReceiptNumber: string;
  ModeOfPayment: string;
  CreatedDate: string;
}

// One row per fee line-item paid in a transaction — generateFeeReceipt's
// receiptInfo is an ARRAY of these, not a URL (confirmed against a real
// response; the mobile app assembles this into an HTML receipt on-device,
// see FeeReceiptHTMLContent in the parent app's AppStrings).
export interface ReceiptRow {
  schoolName: string;
  schoolImage: string | null;
  address: string;
  // NOT yet confirmed against a real generateFeeReceipt response — added
  // speculatively so the receipt template can show school contact details
  // once the backend confirms these field names (or adds them). Rendered
  // conditionally so their absence today doesn't break anything.
  schoolPhone?: string | null;
  schoolEmail?: string | null;
  ReceiptNumber: string;
  studentName: string;
  parentName: string;
  classNumber: string;
  classDivision: string;
  registrationId: string;
  paymentId: string;
  ModeOfPayment: string;
  CreatedDate: string;
  currencyType: string;
  TermName?: string;
  name?: string;
  ActualAmount: number;
  DiscountAmount: number;
  Amount: number;
  lateFees?: number;
}

export interface PaymentGateway {
  paymentGatewayLogo: string;
  paymentGatewayName: string;
  pkPaymentGatewayId: number;
}

// Line-item shape sent in feesTermArray/feesTypeArray/otherFeesArray —
// which fields apply depends on isTypeOrTerm (see buildPaymentLineItem in
// the component). All three kinds carry the same envelope fields
// (amount/isTypeOrTerm/totalLateFee/fkLateFeeId/discount/fkFeeStructuresId);
// only the id field differs (termId vs feesTypeId + bus fields vs
// pkOtherFeesId/pkOtherFeeTermId).
export interface PaymentLineItem {
  amount: number;
  isTypeOrTerm: 'TERM' | 'TYPE' | 'OTHER';
  totalLateFee: number;
  fkLateFeeId: number | null;
  discount: number;
  fkFeeStructuresId: number | null;
  termId?: number;
  feesTypeId?: number | null;
  isBusFee?: boolean;
  busId?: string | null;
  busStopId?: number | null;
  busTermId?: number | null;
  pkOtherFeesId?: number;
  pkOtherFeeTermId?: number;
}

export interface GetRedirectionUrlBody {
  fkClassId: string;
  emailId: string;
  feesTermArray: PaymentLineItem[];
  feesTypeArray: PaymentLineItem[];
  otherFeesArray: PaymentLineItem[];
  feeType: string;
  totalAmount: number;
  totalLateFee: number;
  fkStudentId: string;
  fkPaymentGatewayId: number;
  lang: string;
  fkLateFeeId: number | null;
}

// Fees — view-only fee list/detail (FeesInformation.js / FeesDetailedView.js
// + Timeline.js) plus payment initiation (PaymentDetails.js): gateway
// selection, Razorpay Checkout.js (the web equivalent of the mobile app's
// native Razorpay SDK), and the generic-gateway status-polling path.
// Instamojo has no native web WebView to detect page-title changes in, so
// it's handled as a plain redirect — see parent-fees-payment.component.ts.
@Injectable({ providedIn: 'root' })
export class ParentFeesService {

  private base = AppSettings.API_ENDPOINT_STAFF_BASE;
  private v3Base = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) { }

  private authHeaders(): HttpHeaders | undefined {
    const raw = localStorage.getItem('authToken');
    if (!raw) { return undefined; }
    try {
      return new HttpHeaders().append('authToken', JSON.parse(raw));
    } catch {
      return new HttpHeaders().append('authToken', raw);
    }
  }

  getFeesList(classId: string, studentId: string, academicId: string) {
    return this.http.post(`${this.base}/students/fee/details`,
      { classId, studentId, academicId }, { headers: this.authHeaders() });
  }

  // Gates opening a fee's detail view — blocks with an "old academic year
  // dues pending" state if there's an outstanding balance from a previous
  // academic year (see FeesInformation.js's itemCheckBalanceAmount).
  checkBalanceAmount(classId: string, studentId: string, academicId: string) {
    return this.http.post(`${this.base}/feeCollection/studentDetails`,
      { classId, studentId, academicId }, { headers: this.authHeaders() });
  }

  // Body shape depends on which fee kind was tapped — mirrors the mobile
  // app's per-fee-kind request exactly (see the API reference this was
  // built from): term fees key off feeTermId, type fees off feesTypeId
  // (+ busTermId/busStopId), other fees off fkOtherFeesId/fkOtherFeeTermId.
  // All three also carry studentId/academicId/schoolId/mobileNumber/
  // fkLateFeeId — note these are NOT the same field names as getFeesList
  // (classId isn't sent at all here).
  getFeesDetail(body: Record<string, any>) {
    return this.http.post(`${this.base}/feesManagement/feeCollectionByTypeOrTermId`,
      body, { headers: this.authHeaders() });
  }

  getFeeReceipt(receiptNumber: string, academicId: string) {
    return this.http.post(`${this.base}/feesManagement/generateFeeReceipt`,
      { receiptNumber, academicId }, { headers: this.authHeaders() });
  }

  getPaymentGateway(fkClassId: string, academicId: string, lang: string) {
    return this.http.get(`${this.v3Base}/get/payment/gateway/list/for/student/academic/level`, {
      params: { feeType: 'schoolFees', fkClassId, academicId, lang },
      headers: this.authHeaders(),
    });
  }

  getRedirectionUrl(body: GetRedirectionUrlBody) {
    return this.http.post(`${this.v3Base}/student/online/school/fee/payment`,
      body, { headers: this.authHeaders() });
  }

  verifyRazorpayPayment(razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string) {
    return this.http.post(`${this.v3Base}/razorpay/verify/payment`,
      { razorpay_payment_id, razorpay_order_id, razorpay_signature }, { headers: this.authHeaders() });
  }

  // status_code 200 = success, 402 = failure (see the API reference this
  // was built from) — polled every 5s for up to 5 minutes on the
  // generic-gateway path.
  getPaymentStatus(paymentRequestId: string, fkStudentId: string, fkClassId: string, lang: string) {
    return this.http.get(`${this.v3Base}/get/student/online/payment/status`, {
      params: { paymentRequestId, fkStudentId, fkClassId, lang },
      headers: this.authHeaders(),
    });
  }
}
